import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  InputAdornment,
  Tabs,
  Tab,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormControlLabel
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  LockReset as LockResetIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { staffAPI } from '../services/api';
import api from '../services/api';

import ExcelImport from '../components/ExcelImport';
import { FileDownload as ImportIcon } from '@mui/icons-material';

const Staff = () => {
  const [loading, setLoading] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [roles, setRoles] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [openImport, setOpenImport] = useState(false);
  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [openResetDialog, setOpenResetDialog] = useState(false);
  const [resetTargetStaff, setResetTargetStaff] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    gender: 'female',
    role: 'teacher',
    designation: '',
    department: '',
    employeeId: '',
    qualification: '',
    experience: '',
    joiningDate: new Date().toISOString().split('T')[0],
    salaryTotal: '',
    addressStreet: '',
    addressCity: '',
    addressState: '',
    addressZipCode: '',
    isClassTeacher: false,
    classTeacherOfClassId: '',
    classTeacherOfSectionId: '',
    teachingSubjects: [],
    employmentType: 'permanent'
  });

  useEffect(() => {
    fetchStaff();
    fetchRoles();
    fetchClassesAndSubjects();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await api.get('/rbac/roles');
      setRoles(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    }
  };

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const response = await staffAPI.getAll();
      setStaffList(response.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch staff list');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchClassesAndSubjects = async () => {
    try {
      const clsRes = await api.get('/classes');
      setClasses(clsRes.data.data || []);
      const subjRes = await api.get('/subjects');
      setSubjects(subjRes.data.data || []);
    } catch (e) {
      console.error('Failed to load classes or subjects:', e);
    }
  };

  const fetchSectionsForClass = async (classId) => {
    if (!classId) {
      setSections([]);
      return;
    }
    try {
      const res = await api.get(`/classes/${classId}/sections`);
      setSections(res.data.data || []);
    } catch (e) {
      console.error('Failed to fetch sections:', e);
    }
  };

  const handleOpenDialog = (staff = null) => {
    if (staff) {
      setEditingStaff(staff);
      if (staff.classTeacherOfClassId) {
        fetchSectionsForClass(staff.classTeacherOfClassId);
      } else {
        setSections([]);
      }
      setFormData({
        firstName: staff.user?.firstName || '',
        lastName: staff.user?.lastName || '',
        email: staff.user?.email || '',
        phone: staff.user?.phone || '',
        dateOfBirth: staff.user?.dateOfBirth ? staff.user.dateOfBirth.split('T')[0] : '',
        gender: staff.user?.gender || 'female',
        role: staff.user?.role || 'teacher',
        designation: staff.designation || '',
        department: staff.department || '',
        employeeId: staff.employeeId || '',
        qualification: staff.qualification || '',
        experience: staff.experience || '',
        joiningDate: staff.joiningDate ? staff.joiningDate.split('T')[0] : '',
        salaryTotal: staff.salaryTotal || '',
        addressStreet: staff.user?.addressStreet || '',
        addressCity: staff.user?.addressCity || '',
        addressState: staff.user?.addressState || '',
        addressZipCode: staff.user?.addressZipCode || '',
        isClassTeacher: staff.isClassTeacher || false,
        classTeacherOfClassId: staff.classTeacherOfClassId || '',
        classTeacherOfSectionId: staff.classTeacherOfSectionId || '',
        teachingSubjects: Array.isArray(staff.teachingSubjects) ? staff.teachingSubjects : (staff.teachingSubjects ? JSON.parse(typeof staff.teachingSubjects === 'string' ? staff.teachingSubjects : JSON.stringify(staff.teachingSubjects)) : []),
        employmentType: staff.employmentType || 'permanent'
      });
    } else {
      setEditingStaff(null);
      setSections([]);
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        dateOfBirth: '',
        gender: 'female',
        role: 'teacher',
        designation: '',
        department: '',
        employeeId: '',
        qualification: '',
        experience: '',
        joiningDate: new Date().toISOString().split('T')[0],
        salaryTotal: '',
        addressStreet: '',
        addressCity: '',
        addressState: '',
        addressZipCode: '',
        isClassTeacher: false,
        classTeacherOfClassId: '',
        classTeacherOfSectionId: '',
        teachingSubjects: [],
        employmentType: 'permanent'
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingStaff(null);
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    const val = type === 'checkbox' ? checked : value;
    
    setFormData(prev => {
      const updated = { ...prev, [name]: val };
      if (name === 'classTeacherOfClassId') {
        fetchSectionsForClass(value);
        updated.classTeacherOfSectionId = '';
      }
      return updated;
    });
  };

  const handleSubmit = async () => {
    // Basic validation
    if (!formData.firstName || !formData.lastName || !formData.email) {
      toast.error('Please fill required fields');
      return;
    }

    try {
      if (editingStaff) {
        await staffAPI.update(editingStaff.id, formData);
        toast.success('Staff updated successfully');
      } else {
        // Pass a default password for new staff
        await staffAPI.create({ ...formData, password: '5ch001&M3' });
        toast.success('Staff created successfully');
      }
      handleCloseDialog();
      fetchStaff();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this staff member?')) {
      try {
        await staffAPI.delete(id);
        toast.success('Staff deleted successfully');
        fetchStaff();
      } catch (error) {
        toast.error('Failed to delete staff');
      }
    }
  };

  const handleResetPassword = async () => {
    if (!resetTargetStaff) return;
    try {
      await staffAPI.resetPassword(resetTargetStaff.id, {});
      toast.success(`Password reset to default 5ch001&M3 successfully for ${resetTargetStaff.user?.firstName}`);
      setOpenResetDialog(false);
      setResetTargetStaff(null);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to reset password');
    }
  };

  const handleImportStaff = async (data) => {
    try {
      const res = await staffAPI.bulkImport({ staffMembers: data });
      toast.success(res.data.message);
      fetchStaff();
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Import failed');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get('/staff/import-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'staff_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download template');
      console.error(error);
    }
  };

  const filteredStaff = staffList.filter(staff => {
    const searchLower = searchTerm.toLowerCase();
    const fullName = `${staff.user?.firstName} ${staff.user?.lastName}`.toLowerCase();
    const email = staff.user?.email?.toLowerCase() || '';
    const empId = staff.employeeId?.toLowerCase() || '';

    return fullName.includes(searchLower) || email.includes(searchLower) || empId.includes(searchLower);
  });

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">
          Staff Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleDownloadTemplate}
          >
            Download Template
          </Button>
          <Button
            variant="outlined"
            startIcon={<ImportIcon />}
            onClick={() => setOpenImport(true)}
          >
            Import Excel
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Add Staff
          </Button>
        </Box>
      </Box>

      <Paper sx={{ mb: 3, p: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by name, email or ID..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          size="small"
        />
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>Employee</strong></TableCell>
              <TableCell><strong>ID/Role</strong></TableCell>
              <TableCell><strong>Contact</strong></TableCell>
              <TableCell><strong>Department/Desig.</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : filteredStaff.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  No staff members found
                </TableCell>
              </TableRow>
            ) : (
              filteredStaff.map((staff) => (
                <TableRow key={staff.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'secondary.main' }}>
                        {staff.user?.firstName?.charAt(0) || <PersonIcon />}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {staff.user?.firstName} {staff.user?.lastName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Joined: {staff.joiningDate ? new Date(staff.joiningDate).toLocaleDateString() : '-'}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {staff.employeeId}
                    </Typography>
                    <Chip
                      label={staff.user?.role}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mt: 0.5, textTransform: 'capitalize' }}
                    />
                    {staff.isClassTeacher && staff.classTeacherOf && (
                      <Chip
                        label={`CT: ${staff.classTeacherOf.name} - ${staff.sectionTeacherOf?.name || ''}`}
                        size="small"
                        color="secondary"
                        sx={{ mt: 0.5, ml: 0.5 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <EmailIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2">{staff.user?.email}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <PhoneIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2">{staff.user?.phone || '-'}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{staff.department}</Typography>
                    <Typography variant="caption" color="text.secondary">{staff.designation}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={staff.user?.isActive ? "Active" : "Inactive"}
                      color={staff.user?.isActive ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Reset Password">
                      <IconButton onClick={() => { setResetTargetStaff(staff); setOpenResetDialog(true); }} size="small" color="warning">
                        <LockResetIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton onClick={() => handleOpenDialog(staff)} size="small">
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton onClick={() => handleDelete(staff.id)} size="small" color="error">
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Staff Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingStaff ? 'Edit Staff' : 'Add New Staff'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Personal Information</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="First Name" name="firstName"
                value={formData.firstName} onChange={handleChange} required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Last Name" name="lastName"
                value={formData.lastName} onChange={handleChange} required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Email" name="email" type="email"
                value={formData.email} onChange={handleChange} required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Phone" name="phone"
                value={formData.phone} onChange={handleChange}
                inputProps={{ maxLength: 10, inputMode: 'numeric', pattern: '[0-9]{10}' }}
                helperText="Enter 10-digit mobile number"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Date of Birth" name="dateOfBirth" type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.dateOfBirth} onChange={handleChange} required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Gender</InputLabel>
                <Select name="gender" value={formData.gender} label="Gender" onChange={handleChange}>
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Role</InputLabel>
                <Select name="role" value={formData.role} label="Role" onChange={handleChange}>
                  {roles.map(r => (
                    <MenuItem key={r.id} value={r.slug}>{r.name}</MenuItem>
                  ))}
                  {roles.length === 0 && (
                    <>
                      <MenuItem value="teacher">Teacher</MenuItem>
                      <MenuItem value="admin">Admin</MenuItem>
                      <MenuItem value="clerk">Clerk</MenuItem>
                      <MenuItem value="principal">Principal</MenuItem>
                    </>
                  )}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1 }}>Employment Details</Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Employee ID" name="employeeId"
                value={formData.employeeId} onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Department" name="department"
                value={formData.department} onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth label="Designation" name="designation"
                value={formData.designation} onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Qualification" name="qualification"
                value={formData.qualification} onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Experience" name="experience"
                value={formData.experience} onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Joining Date" name="joiningDate" type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.joiningDate} onChange={handleChange}
              />
            </Grid>
             <Grid item xs={12} sm={6}>
              <TextField
                fullWidth label="Salary (Total)" name="salaryTotal" type="number"
                value={formData.salaryTotal} onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Employment Type</InputLabel>
                <Select
                  name="employmentType"
                  value={formData.employmentType}
                  label="Employment Type"
                  onChange={handleChange}
                >
                  <MenuItem value="permanent">Permanent</MenuItem>
                  <MenuItem value="contract">Contract</MenuItem>
                  <MenuItem value="visiting">Visiting</MenuItem>
                  <MenuItem value="part-time">Part-time</MenuItem>
                  <MenuItem value="probation">Probation</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Teaching Subjects</InputLabel>
                <Select
                  multiple
                  name="teachingSubjects"
                  value={formData.teachingSubjects || []}
                  onChange={handleChange}
                  input={<OutlinedInput label="Teaching Subjects" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {subjects.map((subj) => (
                    <MenuItem key={subj.id} value={subj.name}>
                      <Checkbox checked={(formData.teachingSubjects || []).indexOf(subj.name) > -1} />
                      <ListItemText primary={subj.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="isClassTeacher"
                    checked={formData.isClassTeacher}
                    onChange={handleChange}
                  />
                }
                label="Is Class Teacher?"
              />
            </Grid>
            {formData.isClassTeacher && (
              <>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Class Teacher of Class</InputLabel>
                    <Select
                      name="classTeacherOfClassId"
                      value={formData.classTeacherOfClassId}
                      label="Class Teacher of Class"
                      onChange={handleChange}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {classes.map((c) => (
                        <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <InputLabel>Class Teacher of Section</InputLabel>
                    <Select
                      name="classTeacherOfSectionId"
                      value={formData.classTeacherOfSectionId}
                      label="Class Teacher of Section"
                      onChange={handleChange}
                      disabled={!formData.classTeacherOfClassId}
                    >
                      <MenuItem value=""><em>None</em></MenuItem>
                      {sections.map((s) => (
                        <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}

            {/* Address fields could go here or in a separate tab/section */}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            {editingStaff ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      <ExcelImport
        open={openImport}
        onClose={() => setOpenImport(false)}
        onImport={handleImportStaff}
        title="Import Staff"
        template={{
          firstName: 'Alice',
          lastName: 'Smith',
          email: 'alice@example.com',
          phone: '9876543210',
          gender: 'female',
          dateOfBirth: '1985-05-15',
          designation: 'Teacher',
          department: 'Science',
          salary: 50000,
          joiningDate: '2022-06-01',
          employmentType: 'permanent',
          qualification: 'M.Sc. Physics',
          teachingSubjects: 'Physics, Chemistry',
          isClassTeacher: 'yes',
          classTeacherOfClass: 'Class 4',
          classTeacherOfSection: 'A',
          password: 'password123'
        }}
      />

      {/* Reset Password Dialog */}
      <Dialog open={openResetDialog} onClose={() => setOpenResetDialog(false)}>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1">
            Are you sure you want to reset the password for <strong>{resetTargetStaff?.user?.firstName} {resetTargetStaff?.user?.lastName}</strong> to the default password <strong>5ch001&M3</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenResetDialog(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={handleResetPassword}>
            Reset Password
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Staff;
